import type { ColumnDef, OnChangeFn, PaginationState, SortingState } from "@tanstack/react-table";
import {
  Download,
  Edit,
  ExternalLink,
  GraduationCap,
  Handshake,
  Mail,
  MonitorPlay,
  Newspaper,
  Rocket,
  Users,
  X,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DataTable } from "@/components/admin/DataTable";
import { SearchInput } from "@/components/admin/SearchInput";
import { type BadgeTone, StatusBadge } from "@/components/admin/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useUpdateCategorization,
  useWebsiteForms,
  type WebsiteFormEntry,
  type WebsiteFormType,
} from "@/hooks/admin/websiteForms";
import { AdminLayout } from "@/layouts/AdminLayout";
import { cn } from "@/lib/utils";
import { api } from "@/services/api";
import type {
  AmbassadorApplication,
  ContactSubmission,
  DominionDemoAccess,
  EventPitch,
  NewsletterSubscriber,
  PartnershipApplication,
  WaitlistEntry,
} from "@/types";

const PAGE_SIZE = 25;

type TabType = WebsiteFormType;
type FormEntry = WebsiteFormEntry;

const tabs: { id: TabType; label: string; icon: React.ElementType }[] = [
  { id: "waitlist", label: "Waitlist", icon: Users },
  { id: "partnerships", label: "Partnerships", icon: Handshake },
  { id: "contacts", label: "Contacts", icon: Mail },
  { id: "newsletter", label: "Newsletter", icon: Newspaper },
  { id: "pitches", label: "Pitches", icon: Rocket },
  { id: "demo-access", label: "Demo Access", icon: MonitorPlay },
  { id: "ambassadors", label: "Ambassadors", icon: GraduationCap },
];

// Columns the backend can sort each list by (mirrors the `sortable` maps in
// routers/website_forms.py). TanStack column ids not in here are display-only.
const SORTABLE: Record<TabType, Set<string>> = {
  waitlist: new Set(["email", "university", "status", "created_at"]),
  partnerships: new Set(["organisation_name", "contact_email", "status", "created_at"]),
  contacts: new Set(["name", "email", "created_at"]),
  newsletter: new Set(["email", "status", "created_at"]),
  pitches: new Set(["startup_name", "founder_email", "status", "created_at"]),
  "demo-access": new Set(["email", "created_at"]),
  ambassadors: new Set(["full_name", "university", "email", "status", "created_at"]),
};

function formatDate(dateString: string) {
  return new Date(dateString).toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

const STATUS_TONES: Record<string, BadgeTone> = {
  pending: "warning",
  Pending: "warning",
  active: "success",
  approved: "success",
  accepted: "success",
  Complete: "success",
  rejected: "danger",
  reviewed: "info",
  submitted: "info",
  under_review: "info",
  unsubscribed: "neutral",
};

function StatusPill({ status }: { status: string }) {
  return (
    <StatusBadge tone={STATUS_TONES[status] ?? "neutral"} className="capitalize">
      {status.replace("_", " ")}
    </StatusBadge>
  );
}

interface DetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  entry: FormEntry | null;
  tabType: TabType;
  onUpdate: () => void;
}

function DetailModal({ isOpen, onClose, entry, tabType, onUpdate }: DetailModalProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [cohort, setCohort] = useState("");
  const [contactStatus, setContactStatus] = useState("Pending");
  const [planType, setPlanType] = useState("");
  const updateCategorization = useUpdateCategorization(tabType);
  const { mutateAsync: mutateCategorization, isPending: isSaving } = updateCategorization;

  useEffect(() => {
    if (entry) {
      setCohort(entry.cohort || "");
      setContactStatus(entry.contact_status || "Pending");
      setPlanType(entry.plan_type || "");
    }
  }, [entry]);

  if (!isOpen || !entry) return null;

  const handleSave = async () => {
    try {
      await mutateCategorization({
        id: entry.id,
        data: {
          cohort: cohort || null,
          contact_status: contactStatus,
          plan_type: planType || null,
        },
      });
      setIsEditing(false);
      onUpdate();
    } catch (error) {
      console.error("Failed to update categorization:", error);
      alert("Failed to update. Please try again.");
    }
  };

  const renderEntryDetails = () => {
    switch (tabType) {
      case "waitlist": {
        const waitlistEntry = entry as WaitlistEntry;
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="mt-1 text-gray-900">{waitlistEntry.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">University</p>
              <p className="mt-1 text-gray-900">{waitlistEntry.university}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Degree</p>
              <p className="mt-1 text-gray-900">{waitlistEntry.degree}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Graduation Year</p>
              <p className="mt-1 text-gray-900">{waitlistEntry.graduation_year}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Preferred Field</p>
              <p className="mt-1 text-gray-900 capitalize">
                {waitlistEntry.preferred_field || "-"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Heard From</p>
              <p className="mt-1 text-gray-900">{waitlistEntry.heard_from || "-"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1 text-gray-900 capitalize">{waitlistEntry.status}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">GDPR Consent</p>
              <p className="mt-1 text-gray-900">{waitlistEntry.gdpr_consent ? "Yes" : "No"}</p>
            </div>
          </div>
        );
      }

      case "partnerships": {
        const partnershipEntry = entry as PartnershipApplication;
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Organisation</p>
                <p className="mt-1 text-gray-900">{partnershipEntry.organisation_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Contact Email</p>
                <p className="mt-1 text-gray-900">{partnershipEntry.contact_email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Member Count</p>
                <p className="mt-1 text-gray-900">{partnershipEntry.member_count}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Selected Tier</p>
                <p className="mt-1 text-gray-900 capitalize">
                  {partnershipEntry.selected_tier || "-"}
                </p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <p className="mt-1 text-gray-900 capitalize">{partnershipEntry.status}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500">Goals</p>
              <p className="mt-1 text-gray-900 whitespace-pre-wrap">{partnershipEntry.goals}</p>
            </div>
          </>
        );
      }

      case "contacts": {
        const contactEntry = entry as ContactSubmission;
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Name</p>
                <p className="mt-1 text-gray-900">{contactEntry.name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="mt-1 text-gray-900">{contactEntry.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Responded</p>
                <p className="mt-1 text-gray-900">{contactEntry.responded ? "Yes" : "No"}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">GDPR Consent</p>
                <p className="mt-1 text-gray-900">
                  {contactEntry.gdpr_consent == null
                    ? "Not recorded (pre-KC-464)"
                    : contactEntry.gdpr_consent
                      ? "Yes"
                      : "No"}
                </p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500">Message</p>
              <p className="mt-1 text-gray-900 whitespace-pre-wrap">{contactEntry.message}</p>
            </div>
          </>
        );
      }

      case "newsletter": {
        const newsletterEntry = entry as NewsletterSubscriber;
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="mt-1 text-gray-900">{newsletterEntry.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1 text-gray-900 capitalize">{newsletterEntry.status}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">GDPR Consent</p>
              <p className="mt-1 text-gray-900">
                {newsletterEntry.gdpr_consent == null
                  ? "Not recorded (pre-KC-464)"
                  : newsletterEntry.gdpr_consent
                    ? "Yes"
                    : "No"}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Subscribed</p>
              <p className="mt-1 text-gray-900">
                {new Date(newsletterEntry.created_at).toLocaleString()}
              </p>
            </div>
            {newsletterEntry.unsubscribed_at && (
              <div>
                <p className="text-sm font-medium text-gray-500">Unsubscribed</p>
                <p className="mt-1 text-gray-900">
                  {new Date(newsletterEntry.unsubscribed_at).toLocaleString()}
                </p>
              </div>
            )}
          </div>
        );
      }

      case "pitches": {
        const pitchEntry = entry as EventPitch;
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Startup Name</p>
              <p className="mt-1 text-gray-900">{pitchEntry.startup_name}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Founder Email</p>
              <p className="mt-1 text-gray-900">{pitchEntry.founder_email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Status</p>
              <p className="mt-1 text-gray-900 capitalize">{pitchEntry.status}</p>
            </div>
            {pitchEntry.pitch_deck_url && (
              <div>
                <p className="text-sm font-medium text-gray-500">Pitch Deck</p>
                <a
                  href={pitchEntry.pitch_deck_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 text-green-600 hover:text-green-700 inline-flex items-center gap-1"
                >
                  Download <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            )}
          </div>
        );
      }

      case "demo-access": {
        const demoEntry = entry as DominionDemoAccess;
        return (
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-gray-500">Email</p>
              <p className="mt-1 text-gray-900">{demoEntry.email}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Marketing Consent</p>
              <p className="mt-1 text-gray-900">{demoEntry.consent ? "Given" : "Not given"}</p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Unlocked</p>
              <p className="mt-1 text-gray-900">
                {new Date(demoEntry.created_at).toLocaleString()}
              </p>
            </div>
          </div>
        );
      }

      case "ambassadors": {
        const ambassadorEntry = entry as AmbassadorApplication;
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-500">Full Name</p>
                <p className="mt-1 text-gray-900">{ambassadorEntry.full_name}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">University</p>
                <p className="mt-1 text-gray-900">{ambassadorEntry.university}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Email</p>
                <p className="mt-1 text-gray-900">{ambassadorEntry.email}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">LinkedIn</p>
                <a
                  href={ambassadorEntry.linkedin_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="mt-1 text-green-600 hover:text-green-700 inline-flex items-center gap-1"
                >
                  Profile <ExternalLink className="w-3 h-3" />
                </a>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Status</p>
                <p className="mt-1 text-gray-900 capitalize">{ambassadorEntry.status}</p>
              </div>
            </div>
            <div className="mt-4">
              <p className="text-sm font-medium text-gray-500">Why they want to be an ambassador</p>
              <p className="mt-1 text-gray-900 whitespace-pre-wrap">{ambassadorEntry.motivation}</p>
            </div>
          </>
        );
      }

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto m-4">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 px-6 py-4 flex items-center justify-between">
          <h2 className="text-xl font-bold text-gray-900">Submission Details</h2>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Entry Details */}
          <div className="pb-6 border-b border-gray-200">{renderEntryDetails()}</div>

          {/* Categorization Section */}
          <div>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Categorization</h3>
              {!isEditing && (
                <Button size="sm" onClick={() => setIsEditing(true)}>
                  <Edit className="w-4 h-4" />
                  Edit
                </Button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Cohort</p>
                {isEditing ? (
                  <Input
                    value={cohort}
                    onChange={(e) => setCohort(e.target.value)}
                    placeholder="e.g., March 2026"
                  />
                ) : (
                  <p className="text-gray-900 py-2">{cohort || "-"}</p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Contact Status</p>
                {isEditing ? (
                  <Select value={contactStatus} onValueChange={setContactStatus}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Pending">Pending</SelectItem>
                      <SelectItem value="Complete">Complete</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-gray-900 py-2">
                    <StatusPill status={contactStatus} />
                  </p>
                )}
              </div>

              <div>
                <p className="text-sm font-medium text-gray-700 mb-2">Plan Type</p>
                {isEditing ? (
                  <Select
                    value={planType || "none"}
                    onValueChange={(v) => setPlanType(v === "none" ? "" : v)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Select...</SelectItem>
                      <SelectItem value="Paid">Paid</SelectItem>
                      <SelectItem value="Discount">Discount</SelectItem>
                      <SelectItem value="Free">Free</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-gray-900 py-2">{planType || "-"}</p>
                )}
              </div>
            </div>

            {isEditing && (
              <div className="flex gap-3 mt-6 justify-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setIsEditing(false);
                    setCohort(entry.cohort || "");
                    setContactStatus(entry.contact_status || "Pending");
                    setPlanType(entry.plan_type || "");
                  }}
                  disabled={isSaving}
                >
                  Cancel
                </Button>
                <Button onClick={handleSave} disabled={isSaving}>
                  {isSaving ? "Saving..." : "Save Changes"}
                </Button>
              </div>
            )}
          </div>

          {/* Metadata */}
          <div className="pt-4 border-t border-gray-200">
            <p className="text-sm text-gray-500">
              Submitted: {new Date(entry.created_at).toLocaleString()}
            </p>
            <p className="text-sm text-gray-500 mt-1">ID: {entry.id}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function AdminWebsiteFormsPage() {
  const [activeTab, setActiveTab] = useState<TabType>("waitlist");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState("");
  const [sorting, setSorting] = useState<SortingState>([{ id: "created_at", desc: true }]);

  // Modal state
  const [selectedEntry, setSelectedEntry] = useState<FormEntry | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const sort = sorting[0];
  const sortBy = sort?.id ?? "created_at";
  const sortOrder = sort ? (sort.desc ? "desc" : "asc") : "desc";

  const formsQuery = useWebsiteForms(activeTab, {
    page,
    page_size: PAGE_SIZE,
    sort_by: sortBy,
    sort_order: sortOrder as "asc" | "desc",
    q: search || undefined,
  });
  const data = formsQuery.data ?? null;
  const isLoading = formsQuery.isPending;
  const error = formsQuery.isError
    ? formsQuery.error instanceof Error
      ? formsQuery.error.message
      : "Failed to load data"
    : null;
  const { refetch } = formsQuery;

  const handleTabChange = (tab: TabType) => {
    setActiveTab(tab);
    setPage(1);
    setSearch("");
    setSorting([{ id: "created_at", desc: true }]);
  };

  const refreshCurrentTab = async () => {
    const { data: res } = await refetch();
    if (res && selectedEntry) {
      // Re-resolve the open entry from the freshly loaded page (if still present).
      setSelectedEntry(res.items.find((e) => e.id === selectedEntry.id) ?? selectedEntry);
    }
  };

  const handleRowClick = (entry: FormEntry) => {
    setSelectedEntry(entry);
    setIsModalOpen(true);
  };

  const handleExportCSV = async () => {
    try {
      let result: { blob: Blob; filename: string };
      switch (activeTab) {
        case "waitlist":
          result = await api.exportWaitlistCSV();
          break;
        case "partnerships":
          result = await api.exportPartnershipsCSV();
          break;
        case "contacts":
          result = await api.exportContactsCSV();
          break;
        case "newsletter":
          result = await api.exportNewsletterCSV();
          break;
        case "pitches":
          result = await api.exportPitchesCSV();
          break;
        case "demo-access":
          result = await api.exportDominionDemoAccessCSV();
          break;
        case "ambassadors":
          result = await api.exportAmbassadorsCSV();
          break;
        default:
          return;
      }

      const url = window.URL.createObjectURL(result.blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = result.filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Failed to export CSV:", error);
      alert("Failed to export CSV. Please try again.");
    }
  };

  const columns = useMemo<ColumnDef<FormEntry>[]>(() => {
    const nameCell = (value: string) => <span className="font-medium text-gray-900">{value}</span>;
    const mutedCell = (value: string) => <span className="text-gray-500">{value || "-"}</span>;
    const cohortCol: ColumnDef<FormEntry> = {
      accessorKey: "cohort",
      header: "Cohort",
      enableSorting: false,
      cell: ({ row }) => mutedCell((row.original as FormEntry).cohort || "-"),
    };
    const contactStatusCol: ColumnDef<FormEntry> = {
      accessorKey: "contact_status",
      header: "Contact Status",
      enableSorting: false,
      cell: ({ row }) => <StatusPill status={(row.original as FormEntry).contact_status} />,
    };
    const planTypeCol: ColumnDef<FormEntry> = {
      accessorKey: "plan_type",
      header: "Plan Type",
      enableSorting: false,
      cell: ({ row }) => mutedCell((row.original as FormEntry).plan_type || "-"),
    };
    const dateCol: ColumnDef<FormEntry> = {
      accessorKey: "created_at",
      header: "Date",
      cell: ({ row }) => (
        <span className="text-gray-500">{formatDate((row.original as FormEntry).created_at)}</span>
      ),
    };

    switch (activeTab) {
      case "waitlist":
        return [
          {
            accessorKey: "email",
            header: "Email",
            cell: ({ row }) => nameCell((row.original as WaitlistEntry).email),
          },
          {
            accessorKey: "university",
            header: "University",
            cell: ({ row }) => mutedCell((row.original as WaitlistEntry).university),
          },
          cohortCol,
          contactStatusCol,
          planTypeCol,
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => <StatusPill status={(row.original as WaitlistEntry).status} />,
          },
          dateCol,
        ];
      case "partnerships":
        return [
          {
            accessorKey: "organisation_name",
            header: "Organisation",
            cell: ({ row }) => nameCell((row.original as PartnershipApplication).organisation_name),
          },
          {
            accessorKey: "contact_email",
            header: "Contact Email",
            cell: ({ row }) => mutedCell((row.original as PartnershipApplication).contact_email),
          },
          cohortCol,
          contactStatusCol,
          planTypeCol,
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => (
              <StatusPill status={(row.original as PartnershipApplication).status} />
            ),
          },
          dateCol,
        ];
      case "contacts":
        return [
          {
            accessorKey: "name",
            header: "Name",
            cell: ({ row }) => nameCell((row.original as ContactSubmission).name),
          },
          {
            accessorKey: "email",
            header: "Email",
            cell: ({ row }) => mutedCell((row.original as ContactSubmission).email),
          },
          cohortCol,
          contactStatusCol,
          planTypeCol,
          {
            accessorKey: "responded",
            header: "Responded",
            enableSorting: false,
            cell: ({ row }) =>
              (row.original as ContactSubmission).responded ? (
                <StatusBadge tone="success">Yes</StatusBadge>
              ) : (
                <StatusBadge tone="warning">No</StatusBadge>
              ),
          },
          dateCol,
        ];
      case "newsletter":
        return [
          {
            accessorKey: "email",
            header: "Email",
            cell: ({ row }) => nameCell((row.original as NewsletterSubscriber).email),
          },
          cohortCol,
          contactStatusCol,
          planTypeCol,
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => (
              <StatusPill status={(row.original as NewsletterSubscriber).status} />
            ),
          },
          { ...dateCol, header: "Subscribed" },
        ];
      case "pitches":
        return [
          {
            accessorKey: "startup_name",
            header: "Startup Name",
            cell: ({ row }) => nameCell((row.original as EventPitch).startup_name),
          },
          {
            accessorKey: "founder_email",
            header: "Founder Email",
            cell: ({ row }) => mutedCell((row.original as EventPitch).founder_email),
          },
          cohortCol,
          contactStatusCol,
          planTypeCol,
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => <StatusPill status={(row.original as EventPitch).status} />,
          },
          dateCol,
        ];
      case "demo-access":
        return [
          {
            accessorKey: "email",
            header: "Email",
            cell: ({ row }) => nameCell((row.original as DominionDemoAccess).email),
          },
          cohortCol,
          contactStatusCol,
          planTypeCol,
          {
            accessorKey: "consent",
            header: "Consent",
            enableSorting: false,
            cell: ({ row }) =>
              (row.original as DominionDemoAccess).consent ? (
                <StatusBadge tone="success">Yes</StatusBadge>
              ) : (
                <StatusBadge tone="warning">No</StatusBadge>
              ),
          },
          { ...dateCol, header: "Unlocked" },
        ];
      case "ambassadors":
        return [
          {
            accessorKey: "full_name",
            header: "Full Name",
            cell: ({ row }) => nameCell((row.original as AmbassadorApplication).full_name),
          },
          {
            accessorKey: "university",
            header: "University",
            cell: ({ row }) => mutedCell((row.original as AmbassadorApplication).university),
          },
          {
            accessorKey: "email",
            header: "Email",
            cell: ({ row }) => mutedCell((row.original as AmbassadorApplication).email),
          },
          cohortCol,
          contactStatusCol,
          planTypeCol,
          {
            accessorKey: "status",
            header: "Status",
            cell: ({ row }) => (
              <StatusPill status={(row.original as AmbassadorApplication).status} />
            ),
          },
          dateCol,
        ];
      default:
        return [];
    }
  }, [activeTab]);

  const pagination: PaginationState = { pageIndex: page - 1, pageSize: PAGE_SIZE };
  const onPaginationChange: OnChangeFn<PaginationState> = (updater) => {
    const next = typeof updater === "function" ? updater(pagination) : updater;
    setPage(next.pageIndex + 1);
  };
  const onSortingChange: OnChangeFn<SortingState> = (updater) => {
    setSorting((prev) => {
      const next = typeof updater === "function" ? updater(prev) : updater;
      const valid = next.filter((s) => SORTABLE[activeTab].has(s.id));
      setPage(1);
      return valid.length ? valid : prev;
    });
  };

  return (
    <AdminLayout>
      <div className="flex h-full flex-col gap-4">
        {/* Form-type tabs */}
        <div className="flex gap-1 overflow-x-auto rounded-lg border border-gray-200 bg-white p-1">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => handleTabChange(tab.id)}
                className={cn(
                  "flex items-center gap-2 whitespace-nowrap rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-white"
                    : "text-gray-600 hover:bg-gray-50 hover:text-gray-900"
                )}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
              </button>
            );
          })}
        </div>

        <DataTable
          className="h-full"
          columns={columns}
          data={data?.items ?? []}
          isLoading={isLoading}
          error={error}
          onRetry={() => refetch()}
          emptyMessage={`No ${activeTab} submissions yet`}
          getRowId={(e) => e.id}
          onRowClick={handleRowClick}
          toolbar={
            <div className="flex items-center justify-between gap-3">
              <SearchInput
                value={search}
                onChange={(v) => {
                  setSearch(v);
                  setPage(1);
                }}
                placeholder={`Search ${activeTab}…`}
              />
              <Button
                variant="outline"
                onClick={handleExportCSV}
                disabled={isLoading || (data?.items.length ?? 0) === 0}
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
            </div>
          }
          manualPagination
          pageCount={data?.total_pages ?? 0}
          rowCount={data?.total ?? 0}
          pagination={pagination}
          onPaginationChange={onPaginationChange}
          manualSorting
          sorting={sorting}
          onSortingChange={onSortingChange}
        />
      </div>

      {/* Detail Modal */}
      <DetailModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        entry={selectedEntry}
        tabType={activeTab}
        onUpdate={refreshCurrentTab}
      />
    </AdminLayout>
  );
}
